import { FaultData } from "@fairfleet/geotab";
import { useMemo } from "react";
import { FleetDeviceSpeed, FleetFaultSummary, summarizeFleet } from "./summarizeFleet";
import { useFleetVehicleFigures } from "./useFleetVehicleFigures";
import { FUEL_CELL_GROUP_NAME } from "@/constants/groupTemplates";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { useDevicesBySearchWithNumDevices } from "@/hooks/devices/useDevicesBySearch";
import { useDiagnosticsByIds } from "@/hooks/diagnostic/useDiagnosticsByIds";
import { useLatestFaultData } from "@/hooks/faultData/useLatestFaultData";
import { useDevicesInGroup } from "@/hooks/groups/useDevicesInGroup";
import { useGroupByName } from "@/hooks/groups/useGroupByName";

/** State of one data source; each page section renders its own loading and error. */
export interface SourceStatus {
  isLoading: boolean;
  isError: boolean;
  /** The failed source is being fetched again: the error stays until that settles. */
  isRetrying: boolean;
  retry: () => void;
}

export interface FleetDashboardData {
  devicesStatus: SourceStatus;
  /** Per-vehicle speed, fuel and trips: `loadedCount` of `totalCount` vehicles are done. */
  vehiclesStatus: SourceStatus & { loadedCount: number; totalCount: number; failedCount: number };
  faultsStatus: SourceStatus & { error: Error | null };
  fuelCellStatus: SourceStatus;
  /** True when the device list hit the request limit, so all device-based figures are partial. */
  isDeviceListTruncated: boolean;
  /** True when the fleet exceeds {@link MAX_DASHBOARD_VEHICLES}: no figures are loaded then. */
  isOverVehicleCap: boolean;
  /** Number of vehicles in the current group filter. */
  vehicleCount: number;
  totalDevices: number;
  activeDevices: number;
  utilizationPercent: number | null;
  fleetMaxSpeedKmh: number | null;
  topSpeeds: FleetDeviceSpeed[];
  combustionFuelUsedLiters: number;
  combustionVehicleCount: number;
  fuelCellVehicleCount: number;
  openFaultCount: number;
  topFaults: FleetFaultSummary[];
}

/**
 * Most vehicles the dashboard loads figures for.
 *
 * The figures need one StatusData series per vehicle and diagnostic plus one Trip search per
 * vehicle, about 3 calls per vehicle against the client's 900 calls per minute. Fleet-wide
 * searches (no device) are not an option: Geotab documents Trip and StatusData searches as
 * efficient only per device, a group filter cannot be put on them, and the paging cursor would
 * sit on timestamps shared by many vehicles. Above this the group filter has to narrow the fleet.
 */
export const MAX_DASHBOARD_VEHICLES = 300;

// Only what the figures read; `serialNumber` is needed because the hook drops devices without one.
const DEVICE_FIELDS = ["id", "name", "serialNumber"];

const NO_DEVICE_IDS: string[] = [];
const NO_FAULTS: FaultData[] = [];

/**
 * Ids of all devices in the fuel cell group, including its subgroups (the same selection the
 * FuelCell page uses). Errors are not swallowed: a failed lookup must not silently classify every
 * H2 vehicle as combustion. A missing group means no fuel cell devices.
 */
function useFuelCellDeviceIds() {
  const group = useGroupByName(FUEL_CELL_GROUP_NAME);
  const devices = useDevicesInGroup(group.data?.id, ["id"], { refetchInterval: false });
  return {
    isTruncated: devices.isTruncated,
    data: useMemo(() => devices.data?.map(device => device.id), [devices.data]),
    isLoading: group.isLoading || devices.isLoading,
    isError: group.isError || devices.isError,
    isRetrying: (group.isError && group.isFetching) || (devices.isError && devices.isFetching),
    retry: () => void (group.isError ? group.refetch() : devices.refetch()),
  };
}

export function useFleetDashboardData(fromDate: Date, toDate: Date): FleetDashboardData {
  const {
    data: deviceResult,
    isLoading: isLoadingDevices,
    isError: isErrorDevices,
    isFetching: isFetchingDevices,
    refetch: refetchDevices,
  } = useDevicesBySearchWithNumDevices("", false, RESULTS_LIMIT_DEVICE, DEVICE_FIELDS, false);
  const devices = useMemo(() => deviceResult?.devices ?? [], [deviceResult]);
  const isOverVehicleCap = devices.length > MAX_DASHBOARD_VEHICLES;
  // Above the cap nothing per vehicle is requested.
  const deviceIds = useMemo(
    () => (isOverVehicleCap ? NO_DEVICE_IDS : devices.map(device => device.id)),
    [devices, isOverVehicleCap],
  );
  const {
    data: fuelCellDeviceIds,
    isTruncated: isFuelCellListTruncated,
    isLoading: isLoadingFuelCell,
    isError: isErrorFuelCell,
    isRetrying: isRetryingFuelCell,
    retry: retryFuelCell,
  } = useFuelCellDeviceIds();

  const isDeviceListTruncated =
    (deviceResult?.total ?? 0) >= RESULTS_LIMIT_DEVICE || !!isFuelCellListTruncated;

  // One query per vehicle. Polling is off for every query of this page: the dashboard describes
  // a period, not the live state.
  const vehicles = useFleetVehicleFigures(
    deviceIds,
    fromDate,
    toDate,
    !isLoadingDevices && deviceIds.length > 0,
  );

  const {
    data: faultData = NO_FAULTS,
    isLoading: isLoadingFaultsBase,
    isPlaceholderData: isPlaceholderFaults,
    isError: isErrorFaults,
    isFetching: isFetchingFaults,
    error: faultsError,
    refetch: refetchFaults,
  } = useLatestFaultData(deviceIds, fromDate, toDate, undefined, undefined, true, true, true, false);
  const isLoadingFaults = isLoadingFaultsBase || isPlaceholderFaults;
  const faultDiagnosticIds = useMemo(
    () => [...new Set(faultData.map(fault => fault.diagnostic.id))],
    [faultData],
  );
  const { data: faultDiagnosticsById = {} } = useDiagnosticsByIds(
    faultDiagnosticIds,
    faultDiagnosticIds.length === 0,
  );

  const summary = useMemo(
    () =>
      summarizeFleet({
        devices,
        trips: vehicles.trips,
        speedData: vehicles.speedData,
        fuelData: vehicles.fuelData,
        faultData,
        fuelCellDeviceIds: fuelCellDeviceIds ?? [],
        faultDiagnosticsById,
      }),
    [devices, vehicles.trips, vehicles.speedData, vehicles.fuelData, faultData, fuelCellDeviceIds, faultDiagnosticsById],
  );

  return {
    ...summary,
    devicesStatus: {
      isLoading: isLoadingDevices,
      isError: isErrorDevices,
      isRetrying: isErrorDevices && isFetchingDevices,
      retry: () => void refetchDevices(),
    },
    vehiclesStatus: {
      isLoading: vehicles.isLoading,
      isError: vehicles.failedCount > 0,
      isRetrying: vehicles.isRetrying,
      retry: () => void vehicles.retryFailed(),
      loadedCount: vehicles.loadedCount,
      totalCount: vehicles.totalCount,
      failedCount: vehicles.failedCount,
    },
    faultsStatus: {
      isLoading: isLoadingFaults,
      isError: isErrorFaults,
      isRetrying: isErrorFaults && isFetchingFaults,
      error: faultsError,
      retry: () => void refetchFaults(),
    },
    fuelCellStatus: {
      isLoading: isLoadingFuelCell,
      isError: isErrorFuelCell,
      isRetrying: isRetryingFuelCell,
      retry: retryFuelCell,
    },
    isDeviceListTruncated,
    isOverVehicleCap,
    vehicleCount: devices.length,
  };
}
